// 表单相关API接口
import { API_CONFIG, showApiMessage, showApiError } from '../config/api';
import { handleApiError, checkResponseError } from '../utils/errorHandler';

const API_BASE_URL = API_CONFIG.BASE_URL;

// 通用请求函数
const request = async (url, options = {}, showToast = true) => {
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  // 添加token到请求头
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, config);
    const data = await response.json();
    
    // 使用新的错误处理工具
    const errorCheck = checkResponseError(response, data);
    if (errorCheck.isError) {
      const result = handleApiError(new Error(errorCheck.message), data, showToast);
      if (result.shouldRedirect) {
        return;
      }
      // 如果已经处理了错误，直接抛出，避免重复处理
      throw new Error(errorCheck.message);
    }

    if (!response.ok) {
      const errorMessage = data.message || '请求失败';
      // 只在没有使用handleApiError的情况下显示错误
      if (showToast) {
        showApiError(errorMessage);
      }
      throw new Error(errorMessage);
    }

    // 显示成功消息
    if (showToast && data.message) {
      // 操作成功，不显示提示
    }

    return data;
  } catch (error) {
    console.error('API请求错误:', error);
    // 避免重复显示错误，只在特定情况下显示
    if (showToast && !error.message.includes('请求失败') && !error.message.includes('认证错误') && !error.message.includes('登录已过期')) {
      showApiError(error);
    }
    throw error;
  }
};

// 获取表单列表
export const getForms = async (params = {}) => {
    // 如果传入了allView参数，添加到查询字符串中
    const queryParams = {...params};
    if (params.allView) {
        queryParams.allView = params.allView;
    }

    const queryString = new URLSearchParams(queryParams).toString();
    return request(`/forms/form_list?${queryString}`);
};

// 获取单个表单详情
export const getForm = async (formId) => {
    return request(`/forms/getFload?formId=${formId}`, {
        method: 'GET',
    });
};

// 创建表单
export const createForm = async (params) => {
    const {FormId, name, formSchema, QRCode, pathUrl, wechatShare, label, region, department} = params;
    const body = {
        FormId,
        name,
        label, // 标签
        region, // 地区
        department, // 部门
        formSchema: JSON.stringify(formSchema),
        QRCode,
        pathUrl,
        wechatShare: JSON.stringify(wechatShare)
    };
    return request('/forms/save_form', {
        method: 'POST',
        body: JSON.stringify(body),
    });
};

// 更新表单
export const updateForm = async (formId, formData) => {
    // formId为表单Id，Id为数据库Id
    const body = {
        formId, // 表单Id
        Id: formData.id, // 数据库Id
        ...formData,
        // 确保formSchema被转换为JSON字符串
        formSchema: formData.formSchema ? JSON.stringify(formData.formSchema) : formData.formSchema,
        wechatShare: JSON.stringify(formData.wechatShare)
    };
    return request('/forms/save_form', {
        method: 'POST',
        body: JSON.stringify(body),
    });
};

// 删除表单
export const deleteForm = async (id) => {
    return request(`/forms/delete_form?id=${id}`, {
        method: 'GET',
    });
};

// 生成表单链接
export const generateFormLink = async (formId) => {
    return request(`/forms/${formId}/link`, {
        method: 'POST',
    });
};

// 获取表单分享链接
export const getFormShareLink = async (formId) => {
    try {
        const res = await request(`/forms/${formId}/share-link`);
        return res;
    } catch (error) {
        console.error('获取表单分享链接失败:', error);
        // 如果API失败，返回默认链接
        return {
            data: {
                shareUrl: `/detail/${formId}`,
                qrCode: null
            }
        };
    }
};

// 获取表单提交数据
export const getFormSubmissions = async (formId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/forms/${formId}/submissions?${queryString}`);
};

// 获取表单提交统计
export const getFormSubmissionStats = async (formId) => {
    return request(`/forms/${formId}/submission-stats`);
};

/**
 * 导出表单数据为Excel
 * @param {string} formId - 表单ID
 * @param {Object} options - 导出选项
 * @returns {Promise<Blob>} Excel文件blob
 */
export const exportFormData = async (formId, options = {}) => {
    const {
        startDate,
        endDate,
        page = 1,
        limit = 1000,
        showToast = true
    } = options;

    try {
        // 构建查询参数
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        params.append('page', page);
        params.append('limit', limit);

        const url = `/forms/export/${formId}/?${params.toString()}`;

        // 获取认证token
        const token = localStorage.getItem('token');
        const headers = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        console.log('导出Excel请求:', `${API_BASE_URL}${url}`);

        const response = await fetch(`${API_BASE_URL}${url}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            throw new Error(`导出失败: ${response.status} ${response.statusText}`);
        }

        // 检查响应类型
        const contentType = response.headers.get('content-type');
        const contentDisposition = response.headers.get('content-disposition');

        console.log('导出响应类型:', contentType);
        console.log('导出响应头:', contentDisposition);

        // 获取文件名
        let filename = `form_${formId}_data.xlsx`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
            }
        }

        // 获取文件blob
        const blob = await response.blob();

        if (showToast) {
            // Excel文件导出成功，不显示提示
        }

        return {
            blob,
            filename,
            contentType
        };
    } catch (error) {
        console.error('导出Excel失败:', error);
        if (showToast) {
            showApiError(`导出失败: ${error.message}`);
        }
        throw error;
    }
};

/**
 * 下载Excel文件
 * @param {Blob} blob - 文件blob
 * @param {string} filename - 文件名
 */
export const downloadExcel = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * 导出并下载Excel文件
 * @param {string} formId - 表单ID
 * @param {Object} options - 导出选项
 */
export const exportAndDownloadExcel = async (formId, options = {}) => {
    try {
        const result = await exportFormData(formId, {...options, showToast: false});
        downloadExcel(result.blob, result.filename);

        if (options.showToast !== false) {
            // Excel文件下载成功，不显示提示
        }
    } catch (error) {
        console.error('导出下载失败:', error);
        if (options.showToast !== false) {
            showApiError(`导出下载失败: ${error.message}`);
        }
        throw error;
    }
};

// 获取表单统计信息
export const getFormStats = async (formId) => {
    return request(`/forms/${formId}/stats`);
};

// 更新表单状态
export const updateFormStatus = async ({id, isClose, isTop}) => {
    return request('/forms/update_status', {
        method: 'POST',
        body: JSON.stringify({id, isClose, isTop}),
    });
};

// 更新表单其他信息
export const updateFormOther = async ({id, isClose, isTop, label}) => {
    const params = new URLSearchParams();
    params.append('id', id);
    if (isClose !== undefined) params.append('isClose', isClose);
    if (isTop !== undefined) params.append('isTop', isTop);
    if (label !== undefined) params.append('label', label);
    return request(`/forms/update_by_other?${params.toString()}`, {
        method: 'GET',
    });
};

// 提交表单数据
export const submitForm = async (submissionData) => {
    return request('/forms/submit', {
        method: 'POST',
        body: JSON.stringify(submissionData),
    });
};

// 获取配置项（小区、品牌商家等）
export const getConfigurationInformation = async () => {
    return request('/forms/getConfigurationInformation', {method: 'GET'});
};

// 获取表单填写记录列表
export const getFormSubList = async (formId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/forms/get_sub_list/${formId}?${queryString}`);
};

// 获取表单通知列表
export const getNotifierList = async (formId) => {
    return request(`/forms/getNotifierList/${formId}`, {
        method: 'GET',
    });
};

// 保存表单通知设置
export const saveNotifier = async (formId, ids, showToast = true) => {
    // 如果 ids 没有值（空字符串、undefined、null），则传 null
    const idsParam = (!ids || (typeof ids === 'string' && ids.trim() === '')) ? 'null' : ids;
    return request(`/forms/saveNotifier/${formId}/${idsParam}`, {
        method: 'GET',
    }, showToast);
};

// 获取转盘数据
export const getWheelData = async (formId) => {
    return request(`/forms/getWheelData/${formId}`, {
        method: 'GET',
    });
};